import React, { useMemo, useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import FormInputs from "../UI/FormInputs";
import Buttons from "../UI/Buttons";
import { isSet } from "../../utils/commonUtils";

/**
 * Reusable modal to setup PIN + Signature for logged-in user.
 * Designed to be used anywhere (ProviderSignOff, other flows).
 */
const PinSignatureSetupModal = ({
  isOpen,
  onClose,
  onSave,
  Toast,
  Loader,
  title = "Set PIN & Signature",
}) => {
  const [pin, setPin] = useState("");
  const [signature, setSignature] = useState("");
  const [saving, setSaving] = useState(false);
  const [validation, setValidation] = useState({});
  const [formKey, setFormKey] = useState(0);

  // Reset form whenever the modal is opened so it's blank every time
  useEffect(() => {
    if (isOpen) {
      setPin("");
      setSignature("");
      setValidation({});
      setFormKey((k) => k + 1); // force signature (canvas) to remount and clear
    }
  }, [isOpen]);

  const canSubmit = useMemo(() => {
    return Boolean(pin && String(pin).trim() !== "" && signature);
  }, [pin, signature]);

  const submit = async () => {
    const v = {};
    const pinStr = String(pin || "").trim();
    if (!pinStr) v.pin = "PIN is required";
    else if (pinStr.length !== 4) v.pin = "PIN must be 4 digits";
    if (!signature) v.signature = "Signature is required";
    setValidation(v);
    if (Object.keys(v).length) return;

    setSaving(true);
    if (Loader) Loader(true);
    try {
      await onSave?.({ pin: String(pin).trim(), signature });
      Toast?.success?.("PIN & Signature saved");
      setPin("");
      setSignature("");
      setValidation({});
      onClose?.();
    } catch (e) {
      const msg =
        isSet(e?.data?.message, "") ||
        isSet(e?.message, "") ||
        "Failed to save PIN & Signature";
      Toast?.error?.(msg);
    } finally {
      setSaving(false);
      if (Loader) Loader(false);
    }
  };

  return (
    <Modal
      show={Boolean(isOpen)}
      onHide={() => (saving ? null : onClose?.())}
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header closeButton={!saving}>
        <Modal.Title>
          <h6 className="text-lg mb-0 font-weight-bold">{title}</h6>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-12 mb-2">
            <label className="mb-0 text-sm opacity-75 text-truncate">
              Enter PIN
            </label>
            <FormInputs
              type="Password"
              className="form-control form-control-solid"
              placeholder="Enter PIN (4 digits)"
              name="pin"
              value={pin}
              inputProps={{ maxLength: 4 }}
              changeHandler={(val) => {
                const digitsOnly = String(val ?? "").replace(/\D/g, "").slice(0, 4);
                setPin(digitsOnly);
                setValidation((prev) => ({ ...prev, pin: "" }));
              }}
            />
            {isSet(validation.pin, "") !== "" ? (
              <span className="text-danger m-1">{validation.pin}</span>
            ) : null}
          </div>

          <div className="col-12">
            <label className="mb-0 text-sm opacity-75 text-truncate">
              Signature
            </label>
            <FormInputs
              key={formKey}
              type="NewSignature"
              className="form-control form-control-solid"
              name="signature"
              value={signature}
              changeHandler={(val) => {
                setSignature(val);
                setValidation((prev) => ({ ...prev, signature: "" }));
              }}
            />
            {isSet(validation.signature, "") !== "" ? (
              <span className="text-danger m-1">{validation.signature}</span>
            ) : null}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-center">
        <Buttons
          type="Single"
          className="btn btn-primary"
          label="Save"
          disabled={!canSubmit || saving}
          clickHandler={submit}
          acl="any"
        />
      </Modal.Footer>
    </Modal>
  );
};

export default PinSignatureSetupModal;

